import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sparkle, ChartBar, User, ChatCircle, ArrowRight, X, Check
} from "@phosphor-icons/react";

const STORAGE_KEY = "aadhyaa_quickstart_seen";

const STEPS = [
  {
    id: 1,
    title: "Check your score",
    description: "See your personalized daily alignment score — how the cosmos aligns with your birth chart today.",
    icon: ChartBar,
    color: "var(--burning-flame)",
    bg: "var(--burning-flame)",
    path: "/home",
    cta: "View My Score",
  },
  {
    id: 2,
    title: "Explore your life",
    description: "Discover your core traits, career insights, health guidance, and life patterns — all personalized to your chart.",
    icon: User,
    color: "var(--truffle)",
    bg: "var(--palladian)",
    path: "/my-life",
    cta: "Explore My Life",
  },
  {
    id: 3,
    title: "Ask a question",
    description: "Get instant answers about your career, relationships, health, or any life decision — powered by Vedic intelligence.",
    icon: ChatCircle,
    color: "var(--palladian-text)",
    bg: "var(--palladian)",
    path: "/ask",
    cta: "Ask AADHYAA",
  },
];

const QuickStartGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [exiting, setExiting] = useState(false);

  // Only show on the main dashboard. Other pages (ask, family, reports, my-life)
  // were getting blocked by this overlay during user testing / e2e runs.
  const showOnThisRoute = location.pathname === "/dashboard";

  useEffect(() => {
    if (!showOnThisRoute) {
      setVisible(false);
      return;
    }
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [showOnThisRoute]);

  const dismiss = () => {
    setExiting(true);
    localStorage.setItem(STORAGE_KEY, "true");
    setTimeout(() => setVisible(false), 300);
  };

  const goToStep = (path) => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
    navigate(path);
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      dismiss();
    }
  };

  if (!visible) return null;

  const step = STEPS[currentStep];

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[20000] flex items-end sm:items-center justify-center"
          data-testid="quickstart-overlay"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[var(--truffle)]/70 backdrop-blur-sm" onClick={dismiss} />

          {/* Card */}
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 bg-[var(--bg-surface)] rounded-2xl shadow-2xl overflow-hidden"
            data-testid="quickstart-card"
          >
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4">
              <button
                onClick={dismiss}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-[var(--bg-muted)] transition-colors"
                data-testid="quickstart-dismiss"
                aria-label="Skip guide"
              >
                <X size={18} className="text-[var(--text-muted)]" />
              </button>

              <div className="flex items-center gap-2 mb-1">
                <Sparkle size={16} className="text-[var(--burning-flame)]" weight="fill" />
                <span className="text-xs font-semibold text-[var(--burning-flame)] uppercase tracking-widest">Quick Start</span>
              </div>
              <h2 className="text-lg font-['Cinzel'] text-[var(--text-primary)]">Welcome to AADHYAA</h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">3 steps to unlock your cosmic blueprint</p>
            </div>

            {/* Step Progress Dots */}
            <div className="flex items-center gap-2 px-6 mb-4">
              {STEPS.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                      i < currentStep
                        ? "bg-[var(--palladian)] text-[var(--truffle)]"
                        : i === currentStep
                        ? "bg-[var(--truffle)] text-white"
                        : "bg-[var(--bg-muted)] text-[var(--text-muted)]"
                    }`}
                    data-testid={`quickstart-dot-${i}`}
                  >
                    {i < currentStep ? <Check size={14} weight="bold" /> : i + 1}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 rounded-full transition-colors ${i < currentStep ? "bg-[var(--palladian)]" : "bg-[var(--bg-muted)]"}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Active Step Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.2 }}
                className="px-6 pb-2"
              >
                <div className="flex items-start gap-4 p-4 rounded-xl bg-[var(--bg-muted)] border border-[var(--border-default)]">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${step.bg}20` }}
                  >
                    <step.icon size={24} weight="duotone" style={{ color: step.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--text-primary)] mb-1">{step.title}</p>
                    <p className="text-xs text-[var(--text-muted)] leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Actions */}
            <div className="flex items-center gap-3 px-6 pb-6 pt-3">
              <button
                onClick={() => goToStep(step.path)}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-medium transition-all"
                style={{
                  backgroundColor: "var(--truffle)",
                  color: "white",
                }}
                data-testid="quickstart-go-btn"
              >
                {step.cta}
                <ArrowRight size={16} weight="bold" />
              </button>
              <button
                onClick={nextStep}
                className="py-3 px-4 rounded-xl text-sm font-medium text-[var(--text-muted)] hover:bg-[var(--bg-muted)] transition-colors"
                data-testid="quickstart-next-btn"
              >
                {currentStep < STEPS.length - 1 ? "Next" : "Done"}
              </button>
            </div>

            {/* Skip all */}
            <div className="px-6 pb-4 text-center">
              <button
                onClick={dismiss}
                className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                data-testid="quickstart-skip-all"
              >
                Skip guide — I'll explore on my own
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuickStartGuide;
